import { IRawMaterial } from './component.types';
import { ImageVariant } from './media.types';
import { IBasePartnerCompany } from './partner.types';
import { Coordinates } from './types';

export enum ESiteType {
  FARM = 'FARM',
  MINE = 'MINE',
  FOREST = 'FOREST',
  COLLECTION_SITE = 'COLLECTION_SITE',
  ORIGIN_SITE = 'ORIGIN_SITE',
  PLANT = 'PLANT',
  PROCESSING_SITE = 'PROCESSING_SITE',
  FACTORY = 'FACTORY',
  MANUFACTURING_SITE = 'MANUFACTURING_SITE',
  WAREHOUSE = 'WAREHOUSE',
  HANDLING_SITE = 'HANDLING_SITE',
  GROCERY_STORE = 'GROCERY_STORE',
  DISTRIBUTION_SITE = 'DISTRIBUTION_SITE',
  GENERIC_SITE = 'GENERIC_SITE',
  CLUSTER = 'CLUSTER',
}

export type TSiteType = `${ESiteType}`;
export type SiteTypeWithoutCluster = Exclude<ESiteType, ESiteType.CLUSTER>;

export enum ELandOwnership {
  PRIVATE_OWNERSHIP = 'PRIVATE_OWNERSHIP',
  PUBLIC_OWNERSHIP = 'PUBLIC_OWNERSHIP',
  COMMUNAL_OWNERSHIP = 'COMMUNAL_OWNERSHIP',
  STATE_OWNERSHIP = 'STATE_OWNERSHIP',
  TENANCY = 'TENANCY',
  CONCESSION = 'CONCESSION',
  EASEMENT = 'EASEMENT',
  COLLECTIVE_FARMS = 'COLLECTIVE_FARMS',
  TRUSTS = 'TRUSTS',
  COMMON_PROPERTY_REGIMES = 'COMMON_PROPERTY_REGIMES',
  ADVERSE_POSSESSION = 'ADVERSE_POSSESSION',
  LAND_COOPERATIVES = 'LAND_COOPERATIVES',
  USUFRUCT_RIGHTS = 'USUFRUCT_RIGHTS',
  OTHER = 'OTHER',
}

export type TLandOwnership = `${ELandOwnership}`;

export enum SiteViewOption {
  LIST = 'LIST',
  CLUSTER = 'CLUSTER',
}

export enum TCoordinateFeature {
  UNKOWN = 'UNKOWN',
  COUNTRY = 'COUNTRY',
  REGION = 'REGION',
  POSTCODE = 'POSTCODE',
  DISTRICT = 'DISTRICT',
  PLACE = 'PLACE',
  LOCALITY = 'LOCALITY',
  NEIGHBORHOOD = 'NEIGHBORHOOD',
  STREET = 'STREET',
  ADDRESS = 'ADDRESS',
}

export interface ICoordinateFeature {
  featureType: TCoordinateFeature;
  locationName: string;
  center: Coordinates;
}

export interface IBaseSite {
  siteType: ESiteType;
  id: string;
  title: string;
  createdBy: IBasePartnerCompany;
  ownedBy?: IBasePartnerCompany;
  locationName: string;
  locationCoordinates: Coordinates;
  image?: ImageVariant | null;
  modifiedTimestamp: Date;
  externalId?: string;
  description?: string;
  coordinateFeatures: ICoordinateFeature[];
  inUse: boolean;
  countryCode: string;
  siteClusterId?: string;
}

export type IGenericSite = IBaseSite & { siteType: ESiteType.GENERIC_SITE };

export type IMineSite = IBaseSite & { siteType: ESiteType.MINE };

export type IForestSite = IBaseSite & { siteType: ESiteType.FOREST };

export type ICollectionSite = IBaseSite & { siteType: ESiteType.COLLECTION_SITE };

export type IOriginSite = IBaseSite & { siteType: ESiteType.ORIGIN_SITE };

export type IPlantSite = IBaseSite & { siteType: ESiteType.PLANT };

export type IProcessingSite = IBaseSite & { siteType: ESiteType.PROCESSING_SITE };

export type IFactorySite = IBaseSite & { siteType: ESiteType.FACTORY };

export type IManufacturingSite = IBaseSite & { siteType: ESiteType.MANUFACTURING_SITE };

export type IWarehouseSite = IBaseSite & { siteType: ESiteType.WAREHOUSE };

export type IHandlingSite = IBaseSite & { siteType: ESiteType.HANDLING_SITE };

export type IGroceryStoreSite = IBaseSite & { siteType: ESiteType.GROCERY_STORE };

export type IDistributionSite = IBaseSite & { siteType: ESiteType.DISTRIBUTION_SITE };

export interface IFarmSite extends IBaseSite {
  siteType: ESiteType.FARM;
  sizeHa?: number;
  rawMaterial: IRawMaterial;
  numberOfFarmers?: number;
  additionalRawMaterialIds?: string[];
  landOwnership?: TLandOwnership;
}

export type TSiteTypesWithoutCluster =
  | IFarmSite
  | IGenericSite
  | IMineSite
  | IForestSite
  | ICollectionSite
  | IOriginSite
  | IPlantSite
  | IProcessingSite
  | IFactorySite
  | IManufacturingSite
  | IWarehouseSite
  | IHandlingSite
  | IGroceryStoreSite
  | IDistributionSite;

/**
 * This is a union type of all the different site types that can be part of a cluster.
 */
export type ClusterSitesType<SitesType extends SiteTypeWithoutCluster> =
  SitesType extends ESiteType.FARM
    ? IFarmSite
    : SitesType extends ESiteType.GENERIC_SITE
    ? IGenericSite
    : SitesType extends ESiteType.MINE
    ? IMineSite
    : SitesType extends ESiteType.FOREST
    ? IForestSite
    : SitesType extends ESiteType.COLLECTION_SITE
    ? ICollectionSite
    : SitesType extends ESiteType.ORIGIN_SITE
    ? IOriginSite
    : SitesType extends ESiteType.PLANT
    ? IPlantSite
    : SitesType extends ESiteType.PROCESSING_SITE
    ? IProcessingSite
    : SitesType extends ESiteType.FACTORY
    ? IFactorySite
    : SitesType extends ESiteType.MANUFACTURING_SITE
    ? IManufacturingSite
    : SitesType extends ESiteType.WAREHOUSE
    ? IWarehouseSite
    : SitesType extends ESiteType.HANDLING_SITE
    ? IHandlingSite
    : SitesType extends ESiteType.GROCERY_STORE
    ? IGroceryStoreSite
    : SitesType extends ESiteType.DISTRIBUTION_SITE
    ? IDistributionSite
    : never;

export interface IClusterSite<SitesType extends SiteTypeWithoutCluster = ESiteType.FARM>
  extends Omit<IBaseSite, 'siteClusterId'> {
  siteType: ESiteType.CLUSTER;
  // Can be undefined if the cluster is empty
  sitesType?: SitesType;
  sites: {
    edges: {
      node: ClusterSitesType<SitesType>;
    }[];
    count: number;
    pageInfo: {
      hasNextPage: boolean;
      endCursor: string;
    };
    summary: { workersAmount: number; additionalRawMaterialTitles: string[] };
  };
}

export type ISite = TSiteTypesWithoutCluster | IClusterSite;
